export const AUDIO_WAVEFORM_PEAK_COUNT = 200

const AUDIO_WAVEFORM_DB_NAME = 'audio-waveform-cache-v2'
const AUDIO_WAVEFORM_STORE_NAME = 'waveforms'

export interface AudioWaveformCacheEntry {
  key: string
  peaks: number[]
  duration: number
  sampleCount: number
  url?: string
}

const waveformMemoryCache = new Map<string, AudioWaveformCacheEntry>()

let databasePromise: Promise<IDBDatabase | null> | null = null

function isBrowser() {
  return typeof window !== 'undefined'
}

function requestToPromise<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

function transactionToPromise(transaction: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'))
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'))
  })
}

function openAudioWaveformDatabase() {
  if (!isBrowser() || !('indexedDB' in window)) {
    return Promise.resolve<IDBDatabase | null>(null)
  }

  if (databasePromise) {
    return databasePromise
  }

  databasePromise = new Promise((resolve) => {
    try {
      const request = window.indexedDB.open(AUDIO_WAVEFORM_DB_NAME, 1)

      request.onupgradeneeded = () => {
        const database = request.result
        if (!database.objectStoreNames.contains(AUDIO_WAVEFORM_STORE_NAME)) {
          database.createObjectStore(AUDIO_WAVEFORM_STORE_NAME, { keyPath: 'key' })
        }
      }

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => {
        console.warn('Failed to open IndexedDB, falling back to memory cache', request.error)
        resolve(null)
      }
    } catch (err) {
      console.warn('Failed to open IndexedDB, falling back to memory cache', err)
      resolve(null)
    }
  })

  return databasePromise
}

async function getPersistedAudioWaveform(key: string) {
  try {
    const database = await openAudioWaveformDatabase()
    if (!database) {
      return null
    }

    const transaction = database.transaction(AUDIO_WAVEFORM_STORE_NAME, 'readonly')
    const store = transaction.objectStore(AUDIO_WAVEFORM_STORE_NAME)
    const entry = await requestToPromise(store.get(key)) as AudioWaveformCacheEntry | undefined
    await transactionToPromise(transaction)
    return entry ?? null
  } catch (err) {
    console.warn('IndexedDB get failed:', err)
    return null
  }
}

async function putPersistedAudioWaveform(entry: AudioWaveformCacheEntry) {
  try {
    const database = await openAudioWaveformDatabase()
    if (!database) {
      return
    }

    const transaction = database.transaction(AUDIO_WAVEFORM_STORE_NAME, 'readwrite')
    const store = transaction.objectStore(AUDIO_WAVEFORM_STORE_NAME)
    store.put(entry)
    await transactionToPromise(transaction)
  } catch (err) {
    console.warn('IndexedDB put failed:', err)
  }
}

async function deletePersistedAudioWaveform(key: string) {
  try {
    const database = await openAudioWaveformDatabase()
    if (!database) {
      return
    }

    const transaction = database.transaction(AUDIO_WAVEFORM_STORE_NAME, 'readwrite')
    const store = transaction.objectStore(AUDIO_WAVEFORM_STORE_NAME)
    store.delete(key)
    await transactionToPromise(transaction)
  } catch (err) {
    console.warn('IndexedDB delete failed:', err)
  }
}

function nodeKey(projectId: string, nodeId: string, subTrackIndex?: number) {
  return subTrackIndex != null
    ? `node:${projectId}:${nodeId}:${subTrackIndex}`
    : `node:${projectId}:${nodeId}`
}

function getAudioContextConstructor() {
  if (!isBrowser()) {
    throw new Error('AudioContext is only available in the browser')
  }

  const constructor = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

  if (!constructor) {
    throw new Error('AudioContext is not supported in this browser')
  }

  return constructor
}

async function decodeAudioBuffer(arrayBuffer: ArrayBuffer) {
  const AudioContextConstructor = getAudioContextConstructor()
  const audioContext = new AudioContextConstructor()

  try {
    return await audioContext.decodeAudioData(arrayBuffer.slice(0))
  } finally {
    void audioContext.close().catch(() => undefined)
  }
}

function buildPeaksFromAudioBuffer(audioBuffer: AudioBuffer, count = AUDIO_WAVEFORM_PEAK_COUNT) {
  const peaks = new Array<number>(count).fill(0)
  const segmentSize = Math.max(1, Math.floor(audioBuffer.length / count))

  for (let peakIndex = 0; peakIndex < count; peakIndex += 1) {
    const start = peakIndex * segmentSize
    const end = peakIndex === count - 1 ? audioBuffer.length : Math.min(audioBuffer.length, start + segmentSize)
    const sampleStep = Math.max(1, Math.floor((end - start) / 200))
    let peak = 0

    for (let channelIndex = 0; channelIndex < audioBuffer.numberOfChannels; channelIndex += 1) {
      const channelData = audioBuffer.getChannelData(channelIndex)
      for (let sampleIndex = start; sampleIndex < end; sampleIndex += sampleStep) {
        peak = Math.max(peak, Math.abs(channelData[sampleIndex] ?? 0))
      }
    }

    peaks[peakIndex] = peak
  }

  const maxPeak = Math.max(...peaks, 0)
  if (maxPeak <= 0) {
    return peaks.map(() => 0)
  }

  return peaks.map((peak) => Number((peak / maxPeak).toFixed(3)))
}

async function buildAudioWaveformEntry(
  key: string,
  arrayBuffer: ArrayBuffer,
  url?: string,
): Promise<AudioWaveformCacheEntry> {
  const audioBuffer = await decodeAudioBuffer(arrayBuffer)
  const peaks = buildPeaksFromAudioBuffer(audioBuffer)

  return {
    key,
    peaks,
    duration: Number(audioBuffer.duration.toFixed(3)),
    sampleCount: peaks.length,
    ...(url ? { url } : {}),
  }
}

export function createNodeWaveformKey(projectId: string, nodeId: string, subTrackIndex?: number) {
  return nodeKey(projectId, nodeId, subTrackIndex)
}

export async function extractAudioWaveformFromFile(file: File, key: string) {
  const arrayBuffer = await file.arrayBuffer()
  return buildAudioWaveformEntry(key, arrayBuffer)
}

export async function extractAudioWaveformFromUrl(url: string, key: string) {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch audio file: ${response.status}`)
  }

  const arrayBuffer = await response.arrayBuffer()
  return await buildAudioWaveformEntry(key, arrayBuffer, url)
}

export function getMemoryAudioWaveform(key: string) {
  return waveformMemoryCache.get(key) ?? null
}

export async function cacheAudioWaveform(entry: AudioWaveformCacheEntry, options?: { persist?: boolean }) {
  waveformMemoryCache.set(entry.key, entry)

  if (options?.persist) {
    await putPersistedAudioWaveform(entry)
  }

  return entry
}

export async function getCachedAudioWaveform(key: string) {
  const memoryHit = getMemoryAudioWaveform(key)
  if (memoryHit) {
    return memoryHit
  }

  const persistedHit = await getPersistedAudioWaveform(key)
  if (persistedHit) {
    waveformMemoryCache.set(key, persistedHit)
    return persistedHit
  }

  return null
}

export async function seedAudioWaveformCacheFromSnapshot(options: {
  key: string
  peaks: number[]
  duration: number
}) {
  const entry: AudioWaveformCacheEntry = {
    key: options.key,
    peaks: options.peaks,
    duration: options.duration,
    sampleCount: options.peaks.length,
  }

  await cacheAudioWaveform(entry, { persist: true })
  return entry
}

export async function promoteAudioWaveformCache(projectId: string, nodeId: string, url?: string) {
  const key = nodeKey(projectId, nodeId)
  const sourceEntry = await getCachedAudioWaveform(key)

  if (!sourceEntry) {
    return null
  }

  const promotedEntry: AudioWaveformCacheEntry = {
    ...sourceEntry,
    key,
    ...(url ? { url } : {}),
  }

  await cacheAudioWaveform(promotedEntry, { persist: true })
  return promotedEntry
}

export async function getOrCreateRemoteAudioWaveform(
  projectId: string,
  nodeId: string,
  audioUrl: string,
) {
  const key = nodeKey(projectId, nodeId)

  const cachedEntry = await getCachedAudioWaveform(key)
  if (cachedEntry) {
    if (cachedEntry.url === audioUrl) {
      return cachedEntry
    }
    // URL changed (or it's an old cache without URL), invalidate and rebuild
    await clearNodeWaveformCache(projectId, nodeId)
  }

  const rebuiltEntry = await extractAudioWaveformFromUrl(audioUrl, key)
  await cacheAudioWaveform(rebuiltEntry, { persist: true })
  return rebuiltEntry
}

export async function clearNodeWaveformCache(projectId: string, nodeId: string) {
  const key = nodeKey(projectId, nodeId)
  waveformMemoryCache.delete(key)
  await deletePersistedAudioWaveform(key)
}